import { createSlice } from '@reduxjs/toolkit';
import {
  operationHasPrice,
  shortTermRentalHasPeriodWithPrice,
} from '@/components/Properties/ListingRegistration/steps/PricingStep/utils/operationPriceUtils';
import initialState from './initialState';
import textToHtmlParagraphs, { normalizePricePosition } from './utils';
import {
  createOwner,
  createProperty,
  fetchAmenitiesSchema,
  fetchCharacteristicsSchema,
  checkHasDevelopments,
  fetchDevelopmentTags,
  fetchPropertyTags,
  fetchCotizationSchema,
  fetchListingSupervision,
  fetchPricePosition,
  getContactsBySearch,
  fetchPrefillOwnerContact,
  unlinkAndRestoreTags,
  generateAIDescription,
  getPlanUpgradeStatus,
  requestFullPlan,
  fetchProperty,
  updateProperty,
} from './service';

function syncShortTermRentalEnabled(state) {
  const rental = state.pricing.shortTermRental;

  if (rental.enabled && !shortTermRentalHasPeriodWithPrice(rental)) {
    rental.enabled = false;
  }
}

function setQuoterUrl(state, quoterUrl) {
  if (quoterUrl) {
    state.pricing.cotization.quoterUrl = quoterUrl;
  }
}

function resetDuplicatePropertyCheck(state) {
  state.duplicatePropertyCheck.isLoading = false;
  state.duplicatePropertyCheck.isDuplicate = false;
  state.duplicatePropertyCheck.error = null;
}

export const ListingRegistrationSlice = createSlice({
  name: 'ListingRegistration',
  initialState,
  reducers: {
    moveStep(state, action) {
      const currentNumberStep = state.activeStep.split('-')[1];
      const nextNumberStep = parseInt(currentNumberStep, 10) + 1;
      const previousNumberStep = parseInt(currentNumberStep, 10) - 1;
      const nextStep = `registrationStep-${nextNumberStep}`;
      const previousStep = `registrationStep-${previousNumberStep}`;

      if (action.payload === 'next') {
        state.activeStep = nextStep;
      } else {
        state.activeStep = previousStep;
      }
    },
    resetListingRegistrationState(state) {
      Object.assign(state, initialState);
    },
    resetOwnersContactsList(state) {
      state.owners.contacts = [];
    },
    setSelectedContacts(state, action) {
      const contactToAdd = action.payload;
      const isSelected = Boolean(
        state.owners.selectedContacts.find(
          (contact) => contact.id === contactToAdd.id
        )
      );

      if (isSelected) {
        state.owners.selectedContacts = state.owners.selectedContacts.filter(
          (contact) => contact.id !== contactToAdd.id
        );
      } else {
        state.owners.selectedContacts.push(contactToAdd);
      }
    },
    setLocationSearchType(state, action) {
      state.location.searchType = action.payload;
      state.location.selectedAddress = null;
      state.location.propertyFields = {};
      resetDuplicatePropertyCheck(state);
    },
    setSelectedAddress(state, action) {
      const address = action.payload;

      if (!address) {
        state.location.selectedAddress = null;
        state.location.propertyFields = {};
        resetDuplicatePropertyCheck(state);
        return;
      }

      state.location.selectedAddress = {
        ...address,
        geocoded_neighborhood: address.neighborhood ?? '',
      };
      resetDuplicatePropertyCheck(state);
    },
    updateAddressField(state, action) {
      const { key, value } = action.payload;
      if (state.location.selectedAddress) {
        state.location.selectedAddress[key] = value;
        resetDuplicatePropertyCheck(state);
      }
    },
    setSelectedPropertyType(state, action) {
      state.location.selectedPropertyType = action.payload;
      state.location.propertyFields = {};
      resetDuplicatePropertyCheck(state);
    },
    setPropertyField(state, action) {
      const { key, value } = action.payload;
      state.location.propertyFields[key] = value;
      resetDuplicatePropertyCheck(state);
    },
    setDuplicateCheckLoading(state, action) {
      state.duplicatePropertyCheck.isLoading = action.payload;
    },
    setDuplicateCheckResult(state, action) {
      state.duplicatePropertyCheck.isDuplicate = action.payload.isDuplicate;
      state.duplicatePropertyCheck.error = null;
    },
    setDuplicateCheckError(state, action) {
      state.duplicatePropertyCheck.error = action.payload;
      state.duplicatePropertyCheck.isDuplicate = false;
      state.duplicatePropertyCheck.isLoading = false;
    },
    clearDuplicateCheck(state) {
      resetDuplicatePropertyCheck(state);
    },
    setOwners(state, action) {
      const { key, value } = action.payload;
      state.owners[key] = value;
    },
    setPropertyID(state, action) {
      state.propertyID = action.payload;
    },
    setActiveStep(state, action) {
      state.activeStep = action.payload;
    },
    setShowRegistration(state, action) {
      state.showRegistration = action.payload;
    },
    setDevelopmentField(state, action) {
      const { key, value } = action.payload;
      state.development[key] = value;
    },
    toggleDevelopmentAmenity(state, action) {
      const amenity = action.payload;
      const idx = state.development.amenities.indexOf(amenity);
      if (idx >= 0) {
        state.development.amenities.splice(idx, 1);
      } else {
        state.development.amenities.push(amenity);
      }
    },
    setShowPublish(state, action) {
      state.showPublish = action.payload;
    },
    setPricingField(state, action) {
      const { path, value } = action.payload;
      const keys = path.split('.');
      let target = state.pricing;
      for (let i = 0; i < keys.length - 1; i += 1) {
        target = target[keys[i]];
      }
      const lastKey = keys[keys.length - 1];
      target[lastKey] = value;
      // Drop the stale position while a new price is being typed/debounced,
      // so the bar never shows the new amount with the previous position.
      if (lastKey === 'price' && target.pricePosition) {
        target.pricePosition = null;
      }

      const operationKey = keys[0];
      const isPriceFieldUpdate =
        (keys.length === 2 && lastKey === 'price') ||
        (keys.length === 3 && keys[1] === 'prices');

      if (
        (operationKey === 'sale' || operationKey === 'rent') &&
        isPriceFieldUpdate
      ) {
        const operation = state.pricing[operationKey];

        if (operation.enabled) {
          const isBrazilianCompany =
            state.pricing.cotization?.schema?.is_brazilian_company ?? true;

          if (!operationHasPrice(operation, isBrazilianCompany)) {
            operation.enabled = false;
          }
        }
      }
    },
    togglePricingOperation(state, action) {
      const { operation } = action.payload;
      const op = state.pricing[operation];
      op.enabled = !op.enabled;
      if (op.enabled) op.expanded = true;
    },
    togglePricingExpanded(state, action) {
      const { operation } = action.payload;
      state.pricing[operation].expanded = !state.pricing[operation].expanded;
    },
    addShortTermPeriod(state, action) {
      const { period, price } = action.payload;
      state.pricing.shortTermRental.periods.push({
        id: Date.now(),
        period,
        price,
      });
    },
    removeShortTermPeriod(state, action) {
      const { id } = action.payload;
      state.pricing.shortTermRental.periods =
        state.pricing.shortTermRental.periods.filter((p) => p.id !== id);
      syncShortTermRentalEnabled(state);
    },
    updateShortTermPeriod(state, action) {
      const { id, field, value } = action.payload;
      const period = state.pricing.shortTermRental.periods.find(
        (p) => p.id === id
      );
      if (period) period[field] = value;
      syncShortTermRentalEnabled(state);
    },
    enqueuePictures(state, action) {
      state.pictures.push(...action.payload);
    },
    setPictureStatus(state, action) {
      const { id, status, error } = action.payload;
      const pic = state.pictures.find((p) => p.id === id);
      if (pic) {
        pic.status = status;
        if (error !== undefined) pic.error = error;
        else if (status !== 'error') pic.error = null;
      }
    },
    pictureUploaded(state, action) {
      const {
        id,
        serverId,
        url,
        order,
        isFrontCover,
        isBlueprint,
        showInPdf,
        showInWeb,
      } = action.payload;
      const pic = state.pictures.find((p) => p.id === id);
      if (pic) {
        pic.serverId = serverId;
        pic.url = url;
        pic.order = order;
        pic.isFrontCover = isFrontCover;
        pic.isBlueprint = isBlueprint;
        pic.showInPdf = showInPdf;
        pic.showInWeb = showInWeb;
        pic.status = 'completed';
      }
    },
    setFrontCover(state, action) {
      const targetId = action.payload;
      state.pictures.forEach((pic) => {
        pic.isFrontCover = pic.id === targetId;
      });
    },
    togglePictureField(state, action) {
      const { id, field } = action.payload;
      const pic = state.pictures.find((p) => p.id === id);
      if (pic) {
        pic[field] = !pic[field];
      }
    },
    removePicture(state, action) {
      state.pictures = state.pictures.filter((p) => p.id !== action.payload);
    },
    reorderPictures(state, action) {
      state.pictures = action.payload;
    },
    setDescriptionField(state, action) {
      const { key, value } = action.payload;
      state.description[key] = value;
    },
    setupDescriptionFromProperty(state, action) {
      const {
        publication_title,
        description,
        description_en,
        primaryLanguage = 'es',
      } = action.payload;
      const existingTitle = state.description?.title ?? {};
      const titleValue =
        publication_title ?? existingTitle[primaryLanguage] ?? '';
      state.description.title = {
        ...state.description.title,
        [primaryLanguage]: existingTitle[primaryLanguage] || titleValue,
        en: existingTitle.en || titleValue,
      };
      state.description.htmlContent[primaryLanguage] =
        description ?? state.description?.htmlContent?.[primaryLanguage] ?? '';
      state.description.htmlContent.en =
        description_en ?? state.description?.htmlContent?.en ?? '';
    },
    applyAIDescription(state) {
      const generated = state.description.aiGenerated;
      if (!generated) return;
      const lang = state.description.language;
      state.description.title = {
        ...state.description.title,
        [lang]: generated.title,
      };
      state.description.htmlContent[lang] = generated.htmlDescription;
      state.description.aiWasApplied = true;
    },
    // Auto-generation on view load: overwrite the description only, keep the title.
    applyAIGeneratedDescriptionOnly(state) {
      const generated = state.description.aiGenerated;
      if (!generated) return;
      const lang = state.description.language;
      state.description.htmlContent[lang] = generated.htmlDescription;
      state.description.aiWasApplied = true;
    },
    dismissAIBanner(state) {
      state.description.aiWasApplied = false;
    },
    resetAIGeneration(state) {
      state.description.aiStatus = 'idle';
      state.description.aiGenerated = null;
    },
    resetAIModal(state) {
      state.description.aiStatus = 'idle';
      state.description.aiGenerated = null;
      state.description.aiLength = initialState.description.aiLength;
      state.description.aiStyle = initialState.description.aiStyle;
      state.description.aiGuidelines = initialState.description.aiGuidelines;
    },
    setPublished(state, action) {
      state.publish.isPublished = action.payload;
    },
    /**
     * Set a single characteristic field value (number input, counter, radio).
     * Keyed by the field `name` from the schema.
     * Designed for the upcoming autosave: listen to this action to detect changes.
     */
    setCharacteristicValue(state, action) {
      const { name, value } = action.payload;
      state.characteristics.values[name] = value;
    },
    /**
     * Toggle a characteristic tag id (checkbox widget).
     * Designed for the upcoming autosave: listen to this action to detect changes.
     */
    toggleCharacteristicTag(state, action) {
      const tagId = action.payload;
      const idx = state.characteristics.tags.indexOf(tagId);
      if (idx >= 0) {
        state.characteristics.tags.splice(idx, 1);
      } else {
        state.characteristics.tags.push(tagId);
      }
    },
    setAmenityValue(state, action) {
      const { name, value } = action.payload;
      state.amenities.values[name] = value;
    },
    toggleAmenityTag(state, action) {
      const tagId = action.payload;
      const idx = state.amenities.tags.indexOf(tagId);
      if (idx >= 0) {
        state.amenities.tags.splice(idx, 1);
      } else {
        state.amenities.tags.push(tagId);
      }
    },
    setConciergeAmenities(state, action) {
      const { selectedTagId, conciergeTagIds } = action.payload;
      state.development.amenities = state.development.amenities.filter(
        (id) => !conciergeTagIds.includes(id)
      );
      if (selectedTagId !== null) {
        state.development.amenities.push(selectedTagId);
      }
    },
    setCotizationField(state, action) {
      const { name, key, value } = action.payload;
      if (!state.pricing.cotization.fields[name]) {
        state.pricing.cotization.fields[name] = {
          exempt: true,
          value: '',
          period: null,
          selectedCurrency: null,
        };
      }
      state.pricing.cotization.fields[name][key] = value;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getContactsBySearch.pending, (state) => {
      state.owners.contactsLoading = true;
    });
    builder.addCase(getContactsBySearch.fulfilled, (state, action) => {
      state.owners.contactsLoading = false;
      const { contacts } = action.payload;
      state.owners.contacts = contacts;
    });
    builder.addCase(getContactsBySearch.rejected, (state) => {
      state.owners.contactsLoading = false;
      state.owners.contacts = [];
    });

    builder.addCase(fetchPrefillOwnerContact.fulfilled, (state, action) => {
      const { contacts = [] } = action.payload;

      contacts.forEach((contact) => {
        if (!contact?.id || !contact?.name) return;

        const isAlreadySelected = state.owners.selectedContacts.some(
          (selectedContact) => selectedContact.id === contact.id
        );

        if (!isAlreadySelected) {
          state.owners.selectedContacts.push(contact);
        }
      });
    });

    builder.addCase(createOwner.pending, (state) => {
      state.owners.creatingContact = true;
    });
    builder.addCase(createOwner.fulfilled, (state, action) => {
      // "new contact response" and "get contacts response" are different
      function normalizeData(data) {
        const normalizedData = {
          status: {
            color: '',
            id: data.lead_status,
            name: '',
          },
          picture: '',
          name: data.name,
          contact_info: {
            cellphone: data.cellphone,
            valid_cellphone: data.valid_cellphone,
            email: data.email,
          },
          agent: {
            id: null,
            name: '',
          },
          last_activity: '',
          starred: {
            properties: [],
            developments: [],
          },
          id: data.id,
        };

        return normalizedData;
      }

      const normalizedNewContact = normalizeData(action.payload);

      state.owners.creatingContact = false;
      state.owners.modalOpen = false;
      state.owners.selectedContacts = [
        ...state.owners.selectedContacts,
        normalizedNewContact,
      ];
    });
    builder.addCase(createOwner.rejected, (state) => {
      state.owners.creatingContact = false;
    });

    builder.addCase(createProperty.pending, (state) => {
      state.creatingProperty = true;
    });
    builder.addCase(createProperty.fulfilled, (state, action) => {
      state.creatingProperty = false;
      state.propertyID = action.payload.id;
      state.showRegistration = true;
    });
    builder.addCase(createProperty.rejected, (state) => {
      state.creatingProperty = false;
    });

    builder.addCase(fetchListingSupervision.fulfilled, (state, action) => {
      state.listingSupervision = action.payload;
    });

    builder.addCase(fetchAmenitiesSchema.pending, (state) => {
      state.amenities.schemaStatus = 'loading';
    });
    builder.addCase(fetchAmenitiesSchema.fulfilled, (state, action) => {
      state.amenities.schemaStatus = 'success';
      state.amenities.schema = action.payload;
      state.amenities.values = {};
      state.amenities.tags = [];
    });
    builder.addCase(fetchAmenitiesSchema.rejected, (state) => {
      state.amenities.schemaStatus = 'error';
      state.amenities.schema = null;
    });

    builder.addCase(fetchCharacteristicsSchema.pending, (state) => {
      state.characteristics.schemaStatus = 'loading';
    });
    builder.addCase(fetchCharacteristicsSchema.fulfilled, (state, action) => {
      state.characteristics.schemaStatus = 'success';
      state.characteristics.schema = action.payload;
      // Reset values whenever the schema changes (different property type)
      state.characteristics.values = {};
      state.characteristics.tags = [];
    });
    builder.addCase(fetchCharacteristicsSchema.rejected, (state) => {
      state.characteristics.schemaStatus = 'error';
      state.characteristics.schema = null;
    });

    builder.addCase(checkHasDevelopments.fulfilled, (state, action) => {
      state.development.hasDevelopments = action.payload?.page_info?.total > 0;
    });

    builder.addCase(fetchDevelopmentTags.pending, (state) => {
      state.development.tagsStatus = 'loading';
    });
    builder.addCase(fetchDevelopmentTags.fulfilled, (state, action) => {
      if (
        action.meta.arg.developmentId !==
        state.development.selectedDevelopment?.id
      )
        return;
      const tags = action.payload || [];
      const selectedIds = tags
        .filter((tag) => tag.selected)
        .map((tag) => tag.id);
      state.development.tagsStatus = 'success';
      state.development.availableTags = tags;
      state.development.amenities = selectedIds;
      state.development.developmentTagsSnapshot = selectedIds;
    });
    builder.addCase(fetchDevelopmentTags.rejected, (state) => {
      state.development.tagsStatus = 'error';
      state.development.availableTags = [];
    });

    builder.addCase(fetchPropertyTags.fulfilled, (state, action) => {
      if (!state.development.selectedDevelopment) return;
      const tags = action.payload || [];
      state.development.propertyTagsSnapshot = tags
        .filter((tag) => tag.selected)
        .map((tag) => tag.id);
    });

    builder.addCase(fetchCotizationSchema.pending, (state) => {
      state.pricing.cotization.schemaStatus = 'loading';
    });
    builder.addCase(fetchCotizationSchema.fulfilled, (state, action) => {
      const schema = action.payload;
      state.pricing.cotization.schemaStatus = 'success';
      state.pricing.cotization.schema = schema;
      setQuoterUrl(state, schema.quoter_url);

      const currencies = schema.company_currencies || [];
      const defaultCurrency = currencies.length > 0 ? currencies[0].code : null;

      // Pre-populate sale/rent price and pricePosition from pricing_report (only if not yet set).
      ['sale', 'rent'].forEach((key) => {
        const report = schema[key]?.pricing_report ?? null;
        if (report?.suggested_price && !state.pricing[key].price) {
          state.pricing[key].price = String(report.suggested_price);
        }
        if (report?.position != null && !state.pricing[key].pricePosition) {
          state.pricing[key].pricePosition = normalizePricePosition(report);
        }
      });

      // Initialize only fields that don't already have user-entered data,
      // so a re-fetch (e.g. after a network error recovery) doesn't wipe user input.
      Object.keys(schema.general_fields || {}).forEach((name) => {
        if (state.pricing.cotization.fields[name]) return;
        const field = schema.general_fields[name];
        state.pricing.cotization.fields[name] = {
          exempt: true,
          value: '',
          period: field.type_options ? null : undefined,
          selectedCurrency: defaultCurrency,
        };
      });
    });
    builder.addCase(fetchCotizationSchema.rejected, (state) => {
      state.pricing.cotization.schemaStatus = 'error';
      state.pricing.cotization.schema = null;
      state.pricing.cotization.quoterUrl = null;
    });

    builder.addCase(fetchPricePosition.fulfilled, (state, action) => {
      const { operation, value } = action.meta.arg;
      const op = state.pricing[operation];
      if (!op) return;
      // Ignore stale responses: a slower earlier request must not overwrite the
      // position for a price the user has since changed (last-value-wins).
      if (Number(op.price) !== Number(value)) return;

      const { quoter_url, ...pricePositionPayload } = action.payload;
      setQuoterUrl(state, quoter_url);
      op.pricePosition = normalizePricePosition(pricePositionPayload);
    });
    builder.addCase(fetchPricePosition.rejected, (state, action) => {
      // Leave the position cleared (it was reset on price change) so a failed
      // request never surfaces a stale position; no error UI is shown here.
      const { operation } = action.meta.arg;
      const op = state.pricing[operation];
      if (op) op.pricePosition = null;
    });

    builder.addCase(unlinkAndRestoreTags.pending, (state) => {
      state.development.restoringTags = true;
    });
    builder.addCase(unlinkAndRestoreTags.fulfilled, (state) => {
      state.development.restoringTags = false;
      state.development.developmentTagsSnapshot = [];
      state.development.propertyTagsSnapshot = [];
    });
    builder.addCase(unlinkAndRestoreTags.rejected, (state) => {
      state.development.restoringTags = false;
    });

    builder.addCase(generateAIDescription.pending, (state) => {
      state.description.aiStatus = 'loading';
      state.description.aiGenerated = null;
    });
    builder.addCase(generateAIDescription.fulfilled, (state, action) => {
      const TITLE_MAX_LENGTH = 1000;
      const { publication_title, description } = action.payload;
      state.description.aiStatus = 'success';
      state.description.aiGenerated = {
        title:
          publication_title?.slice(0, TITLE_MAX_LENGTH) ?? publication_title,
        description,
        htmlDescription: textToHtmlParagraphs(description),
      };
    });
    builder.addCase(generateAIDescription.rejected, (state, action) => {
      state.description.aiStatus = action.meta.aborted ? 'idle' : 'error';
    });

    builder.addCase(getPlanUpgradeStatus.fulfilled, (state, action) => {
      // the key is in Spanish on the backend
      const { descripcion_generada_por_ia } = action.payload;
      state.description.wasFullPlanRequested = Boolean(
        descripcion_generada_por_ia
      );
    });
    builder.addCase(requestFullPlan.pending, (state) => {
      state.description.isRequestingFullPlan = true;
    });
    builder.addCase(requestFullPlan.fulfilled, (state) => {
      state.description.isRequestingFullPlan = false;
      state.description.wasFullPlanRequested = true;
    });
    builder.addCase(requestFullPlan.rejected, (state) => {
      state.description.isRequestingFullPlan = false;
    });

    builder.addCase(fetchProperty.pending, (state) => {
      state.propertyDataLoading = true;
    });
    builder.addCase(fetchProperty.fulfilled, (state, action) => {
      state.propertyDataLoading = false;
      state.propertyData = action.payload?.data ?? state.propertyData;
    });
    builder.addCase(fetchProperty.rejected, (state) => {
      state.propertyDataLoading = false;
    });
    builder.addCase(updateProperty.fulfilled, () => {
      // no-op
    });
  },
});

export const {
  moveStep,
  resetListingRegistrationState,
  resetOwnersContactsList,
  setSelectedContacts,
  setLocationSearchType,
  setSelectedAddress,
  updateAddressField,
  setSelectedPropertyType,
  setPropertyField,
  setDuplicateCheckLoading,
  setDuplicateCheckResult,
  setDuplicateCheckError,
  clearDuplicateCheck,
  setOwners,
  setShowRegistration,
  setDevelopmentField,
  toggleDevelopmentAmenity,
  setShowPublish,
  setPricingField,
  togglePricingOperation,
  togglePricingExpanded,
  addShortTermPeriod,
  removeShortTermPeriod,
  updateShortTermPeriod,
  enqueuePictures,
  setPictureStatus,
  pictureUploaded,
  setFrontCover,
  togglePictureField,
  removePicture,
  reorderPictures,
  setDescriptionField,
  setupDescriptionFromProperty,
  applyAIDescription,
  applyAIGeneratedDescriptionOnly,
  dismissAIBanner,
  resetAIGeneration,
  resetAIModal,
  setPublished,
  setPropertyID,
  setActiveStep,
  setCharacteristicValue,
  toggleCharacteristicTag,
  setConciergeAmenities,
  setAmenityValue,
  toggleAmenityTag,
  setCotizationField,
} = ListingRegistrationSlice.actions;

export { fetchPricePosition };

export default ListingRegistrationSlice.reducer;
